import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { usePageTitle } from "@/lib/content";

export default function ResetPassword() {
  usePageTitle("Reset Password — Sitebound", "Set a new password for your Sitebound account.");
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState(token ? "form" : "invalid");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setState("done");
    } catch (err) {
      setError(formatApiError(err));
      setState("form");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="blueprint-grid">
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col justify-center px-4 py-24">
        <div className="mono-label">Account</div>
        <h1 className="font-display mt-4 text-4xl font-extrabold uppercase tracking-tight text-slate-100">
          {state === "done" ? "Password updated" : "Set a new password"}
        </h1>

        {state === "done" ? (
          <>
            <p className="mt-4 text-sm text-slate-400">
              Your password has been changed. Your purchases and download links are unaffected.
            </p>
            <Link
              data-testid="reset-success-signin"
              to="/account"
              className="mt-8 inline-block bg-[#FF5500] px-7 py-3.5 text-center text-sm font-bold uppercase tracking-widest text-white hover:bg-[#E04800]"
            >
              Sign in
            </Link>
          </>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            {state === "invalid" && (
              <p data-testid="reset-invalid-note" className="border border-white/15 bg-[#121418] px-3 py-2 text-sm text-slate-400">
                This reset link is missing or malformed. Request a fresh one below from the sign-in page.
              </p>
            )}
            <div>
              <label className="mono-label mb-2 block" htmlFor="reset-password">New password (min 8 characters)</label>
              <Input
                id="reset-password"
                data-testid="reset-password-input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-white/15 bg-[#0A0B0D] text-slate-100"
                disabled={state === "invalid"}
              />
            </div>
            <div>
              <label className="mono-label mb-2 block" htmlFor="reset-confirm">Confirm new password</label>
              <Input
                id="reset-confirm"
                data-testid="reset-confirm-input"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="border-white/15 bg-[#0A0B0D] text-slate-100"
                disabled={state === "invalid"}
              />
            </div>
            {error && (
              <p data-testid="reset-error" className="border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                {error}
              </p>
            )}
            {state !== "invalid" && (
              <button
                data-testid="reset-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF5500] px-5 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800] disabled:opacity-60"
              >
                {loading ? "Updating…" : "Update password"}
              </button>
            )}
            <p className="text-center text-xs text-slate-500">
              <Link data-testid="reset-back-to-signin" to="/account" className="text-[#FF5500] underline underline-offset-2">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
